/**
 * Per-event seating hook. Self-managed state — NOT wired into the global
 * WeddingContext because seating is per-event data that shouldn't be
 * globally cached (seating pages deal with tens of tables + hundreds of
 * assignments that could poison any consumer only needing dashboard stats).
 *
 * Usage: `const seating = useSeating(weddingId, eventId);`
 * Then call `seating.refresh()` / `seating.createTable(...)` etc.
 */
import { useCallback, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useSeating(weddingId, eventId) {
  const [tables, setTables] = useState([]);
  const [unassignedGuests, setUnassignedGuests] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!weddingId || !eventId) return;
    setLoading(true);
    try {
      const [tablesRes, unassignedRes] = await Promise.all([
        axios.get(`${API}/seating/tables/${weddingId}/${eventId}`, { withCredentials: true }),
        axios.get(`${API}/seating/unassigned/${weddingId}/${eventId}`, { withCredentials: true }),
      ]);
      setTables(tablesRes.data);
      setUnassignedGuests(unassignedRes.data);
    } catch (err) {
      console.error('Error loading seating:', err);
    } finally {
      setLoading(false);
    }
  }, [weddingId, eventId]);

  useEffect(() => { refresh(); }, [refresh]);

  const createTable = useCallback(async (payload) => {
    const { data } = await axios.post(`${API}/seating/tables`, payload, { withCredentials: true });
    await refresh();
    return data;
  }, [refresh]);

  const updateTable = useCallback(async (tableId, payload) => {
    const { data } = await axios.put(`${API}/seating/tables/${tableId}`, payload, { withCredentials: true });
    await refresh();
    return data;
  }, [refresh]);

  const deleteTable = useCallback(async (tableId) => {
    await axios.delete(`${API}/seating/tables/${tableId}`, { withCredentials: true });
    await refresh();
  }, [refresh]);

  const assignGuest = useCallback(async (tableId, guestId) => {
    await axios.post(`${API}/seating/assign`, { table_id: tableId, guest_id: guestId }, { withCredentials: true });
    await refresh();
  }, [refresh]);

  const unassignGuest = useCallback(async (guestId) => {
    await axios.post(`${API}/seating/unassign`, { guest_id: guestId }, { withCredentials: true });
    await refresh();
  }, [refresh]);

  const copyFromEvent = useCallback(async ({ sourceEventId, copyAssignments = true }) => {
    if (!weddingId || !eventId) throw new Error('No active event');
    const { data } = await axios.post(
      `${API}/seating/tables/copy`,
      {
        wedding_id: weddingId,
        source_event_id: sourceEventId,
        target_event_id: eventId,
        copy_assignments: copyAssignments,
      },
      { withCredentials: true },
    );
    await refresh();
    return data;
  }, [weddingId, eventId, refresh]);

  return {
    tables, unassignedGuests, loading,
    refresh, createTable, updateTable, deleteTable, assignGuest, unassignGuest, copyFromEvent,
  };
}
